import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { GUEST_TYPES, type GuestTypeDefaultsMap } from "@/lib/guestType";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";
import type { GuestTypeValue } from "@/types/camping";

export function GuestTypeDefaultsSection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [defaults, setDefaults] = useState<GuestTypeDefaultsMap>(settings.guest_type_defaults);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDefaults(settings.guest_type_defaults);
  }, [settings]);

  function update(
    type: GuestTypeValue,
    field: "min_nights" | "default_nights" | "same_day",
    value: string | boolean,
  ) {
    setDefaults((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]:
          field === "same_day"
            ? Boolean(value)
            : Math.max(1, Number.parseInt(String(value) || "1", 10)),
      },
    }));
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/guest-type-defaults", defaults);
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Gästetyp-Voreinstellungen konnten nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Gästetypen – Buchungszeitraum"
      description="Welche Anreise/Abreise die Buchungsmaske je Gästetyp vorschlägt und wie viele Nächte mindestens gebucht werden. Tagesgäste nutzen „Gleicher Tag“ (1 Nacht im System: Anreise heute, Abreise morgen)."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-left text-sm">
            <thead>
              <tr className="border-b border-separator text-label-secondary">
                <th className="py-2 pr-4">Gästetyp</th>
                <th className="py-2 pr-4">Mindestens Nächte</th>
                <th className="py-2 pr-4">Vorschlag Nächte</th>
                <th className="py-2">Gleicher Tag</th>
              </tr>
            </thead>
            <tbody>
              {GUEST_TYPES.map((t) => (
                <tr key={t.value} className="border-b border-separator/60">
                  <td className="py-3 pr-4 font-medium">{t.label}</td>
                  <td className="py-3 pr-4">
                    <input
                      type="number"
                      min={1}
                      className="eh-input w-24"
                      value={defaults[t.value].min_nights}
                      onChange={(e) => update(t.value, "min_nights", e.target.value)}
                      disabled={!canWrite}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <input
                      type="number"
                      min={1}
                      className="eh-input w-24"
                      value={defaults[t.value].default_nights}
                      onChange={(e) => update(t.value, "default_nights", e.target.value)}
                      disabled={!canWrite}
                    />
                  </td>
                  <td className="py-3">
                    <input
                      type="checkbox"
                      checked={defaults[t.value].same_day}
                      onChange={(e) => update(t.value, "same_day", e.target.checked)}
                      disabled={!canWrite || t.value !== "day_visitor"}
                      title={
                        t.value === "day_visitor"
                          ? "An- und Abreise am selben Kalendertag"
                          : "Nur für Tagesgäste"
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Gästetyp-Voreinstellungen gespeichert."
          error={error}
          label="Gästetypen speichern"
        />
      </form>
    </Card>
  );
}
